import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import { Flex } from "antd";

import type { UploadFileExtended } from "@av/apps/public/jobs/services/apply/types";
import {
  ALLOWED_UPLOAD_DOCUMENT_EXTENSIONS,
  type DocumentUploadValidationError,
  getDocumentUploadValidationError,
  resolveDocumentUploadValidationErrorTranslation,
} from "@av/apps/recruitment/ats-common/services/document-upload-validation";
import { Modal } from "@av/components/atoms/Modal";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Upload } from "@av/components/atoms/upload/Upload";
import { UploadDragger } from "@av/components/atoms/upload/UploadDragger";
import { UploadedDocumentModalFileRow } from "./UploadedDocumentModalFileRow";

import type { UploadFile } from "antd/es/upload/interface";

const { Text } = Typography;

type DocumentUploadModalProps = {
  open: boolean;
  title: string;
  fileList: UploadFileExtended[];
  onCancel: () => void;
  onConfirm: () => void;
  onFilesSelected: (files: UploadFile[]) => void;
  confirmLoading?: boolean;
};

const FILE_ERROR_ID = "job-application-upload-modal-error";

export function DocumentUploadModal({
  open,
  title,
  fileList,
  onCancel,
  onConfirm,
  onFilesSelected,
  confirmLoading,
}: DocumentUploadModalProps) {
  const { t } = useTranslation();

  const [validationError, setValidationError] = useState<DocumentUploadValidationError | undefined>(
    undefined
  );
  const hasValidationError = validationError !== undefined;

  return (
    <Modal
      testIdPrefix="job-application-upload"
      title={title}
      open={open}
      maskClosable={false}
      onCancel={() => {
        setValidationError(undefined);
        onCancel();
      }}
      onOk={() => {
        onConfirm();
        setValidationError(undefined);
      }}
      okText={t("recruitment.common.global_add")}
      cancelText={t("recruitment.common.global_cancel")}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: fileList.length === 0 || hasValidationError }}
      width="430px"
    >
      <Flex vertical gap={16}>
        <UploadDragger
          testIdPrefix="job-application-upload-dropzone-group"
          accessibility={{
            invalid: hasValidationError,
            describedBy: hasValidationError ? FILE_ERROR_ID : undefined,
            label: t("recruitment.jobApplication.documents_clickDragFiles", { count: 1 }),
          }}
          multiple={false}
          maxCount={1}
          fileList={fileList}
          itemRender={(_, file, __, actions) => (
            <UploadedDocumentModalFileRow file={file} onRemove={actions.remove} />
          )}
          onChange={({ fileList: fl }) => {
            setValidationError(undefined);
            onFilesSelected(fl);
          }}
          style={{ width: "100%" }}
          accept={ALLOWED_UPLOAD_DOCUMENT_EXTENSIONS.join(",")}
          beforeUpload={(file) => {
            setValidationError(undefined);

            const nextValidationError = getDocumentUploadValidationError(file);

            if (nextValidationError) {
              setValidationError(nextValidationError);

              return Upload.LIST_IGNORE;
            }

            return false;
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t("recruitment.jobApplication.documents_clickDragFiles", { count: 1 })}
          </p>
          <p className="ant-upload-hint" style={{ marginBottom: 0 }}>
            {t("recruitment.jobApplication.documents_fileTypeAndSizeHint")}
          </p>
        </UploadDragger>
        {validationError && (
          <Text
            id={FILE_ERROR_ID}
            role="alert"
            type="danger"
            testIdPrefix="job-application-upload-modal-error"
          >
            {resolveDocumentUploadValidationErrorTranslation(t, validationError)}
          </Text>
        )}
      </Flex>
    </Modal>
  );
}
